import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    TruckIcon,
    UserIcon,
    MapPinIcon,
    CheckCircleIcon,
    XCircleIcon,
    InformationCircleIcon,
    CalendarIcon,
    ArchiveBoxIcon,
    EyeIcon,
    ArrowsRightLeftIcon,
    ArrowUturnLeftIcon,
    ChartBarIcon,
    ClockIcon,
    ArrowLeftIcon,
    FunnelIcon,
    ArrowsUpDownIcon,
    ChevronLeftIcon,
    PhotoIcon,
    ChevronRightIcon,
    ArrowUpIcon,
    ArrowDownIcon
} from '@heroicons/react/24/outline'
import { AuthContextApi } from '../../../../Context/api'
import { AuthContext } from '../../../../Context/index'
import ZoomableImageModal from '../../../../Components/ZoomableImageModal'

const MovimentacaoCliente = () => {
    const { id } = useParams()
    const { states, setters } = useContext(AuthContext)
    const { getMovimentacoesEntidade, getMovimentacaoId } = useContext(AuthContextApi)
    const navigate = useNavigate()

    // Estados
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCliente, setSelectedCliente] = useState(null)
    const [loading, setLoading] = useState(false)
    const [isViewModalOpen, setIsViewModalOpen] = useState(false)
    const [viewingMovimentacao, setViewingMovimentacao] = useState(null)
    const [isImageModalOpen, setIsImageModalOpen] = useState(false)
    const [expandedImage, setExpandedImage] = useState('')
    const [expandedImageAlt, setExpandedImageAlt] = useState('')
    const [currentPage, setCurrentPage] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)

    // Estados de pesquisa avançada
    const [selectedFilterField, setSelectedFilterField] = useState('produto_modelo')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)

    // Tipos de movimentação com ícones modernos
    const movimentacaoTipos = [
        { value: 0, label: 'Enviado', color: 'bg-gradient-to-r from-blue-400 to-cyan-400', icon: ArrowUpIcon, bgColor: 'from-blue-50 to-cyan-50', textColor: 'text-blue-700' },
        { value: 1, label: 'Retorno', color: 'bg-gradient-to-r from-sky-400 to-blue-500', icon: ArrowDownIcon, bgColor: 'from-sky-50 to-blue-50', textColor: 'text-sky-700' },
        { value: 2, label: 'Encerrada', color: 'bg-gradient-to-r from-blue-500 to-indigo-500', icon: ArrowUturnLeftIcon, bgColor: 'from-blue-50 to-indigo-50', textColor: 'text-blue-700' },
        { value: 3, label: 'Cancelada', color: 'bg-gradient-to-r from-red-400 to-pink-400', icon: XCircleIcon, bgColor: 'from-red-50 to-pink-50', textColor: 'text-red-700' }
    ]

    const sortDirectionOptions = [
        { value: 'asc', label: 'Ascendente' },
        { value: 'desc', label: 'Descendente' }
    ]

    // Opções de filtro
    const filterOptions = [
        { value: 'entidade_destino_nome', label: 'Cliente' },
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'equipamento_nome', label: 'Equipamento' },
        { value: 'fornecedor_nome', label: 'Fornecedor' },
        { value: 'marca_nome', label: 'Marca' },
        { value: 'produto_modelo', label: 'Modelo' },
        { value: 'movimentacao_observacao', label: 'Observação' },
        { value: 'produto_serial', label: 'Serial' }
    ]

    // Opções de ordenação
    const sortOptions = [
        { value: 'entidade_destino_nome', label: 'Cliente' },
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'equipamento_nome', label: 'Equipamento' },
        { value: 'fornecedor_nome', label: 'Fornecedor' },
        { value: 'marca_nome', label: 'Marca' },
        { value: 'produto_modelo', label: 'Modelo' },
        { value: 'produto_serial', label: 'Serial' }
    ]

    const loadMovimentacoes = async (page = currentPage) => {
        setLoading(true)
        try {
            const params = {
                page: page,
                per_page: recordsPerPage,
                sort: selectedSortField,
                sort_dir: sortDirection
            }

            // Adicionar filter e search apenas se não forem vazios
            if (selectedFilterField && selectedFilterField !== '') {
                params.filter = selectedFilterField
            }
            if (searchTerm && searchTerm.trim() !== '') {
                params.search = searchTerm.trim()
            }

            const query = new URLSearchParams(params).toString()
            console.log('Query params:', params)
            console.log('Query string:', query)

            const result = await getMovimentacoesEntidade(query, id)
            if (result) {
                setSelectedCliente(result.cliente)
                setters.setDataMovimentacoes(result.movimentacoes || [])
                setTotalRecords(result.total || result.movimentacoes?.length || 0)
            }
        } catch (error) {
            console.error('Erro ao carregar movimentações:', error)
            toast.error('Erro ao carregar movimentações')
        } finally {
            setLoading(false)
        }
    }

    // Função para buscar movimentações
    const handleSearch = async () => {
        setCurrentPage(0) // Reset para primeira página na busca
        setLoading(true)
        try {
            const params = {
                page: 0,
                per_page: recordsPerPage,
                sort: selectedSortField,
                sort_dir: sortDirection
            }

            // Adicionar filter e search apenas se não forem vazios
            if (selectedFilterField && selectedFilterField !== '') {
                params.filter = selectedFilterField
            }
            if (searchTerm && searchTerm.trim() !== '') {
                params.search = searchTerm.trim()
            }

            const query = new URLSearchParams(params).toString()
            const result = await getMovimentacoesEntidade(query, id)
            if (result) {
                setSelectedCliente(result.cliente)
                setters.setDataMovimentacoes(result.movimentacoes || [])
                setTotalRecords(result.total || result.movimentacoes?.length || 0)
                toast.success('Busca realizada com sucesso!')
            } else {
                toast.error('Nenhuma movimentação encontrada')
                setSelectedCliente(null)
                setters.setDataMovimentacoes([])
                setTotalRecords(0)
            }
        } catch (error) {
            setSelectedCliente(null)
            setters.setDataMovimentacoes([])
            setTotalRecords(0)
        } finally {
            setLoading(false)
        }
    }

    // Função para limpar busca
    const handleClear = () => {
        setSearchTerm('')
        setSelectedFilterField('produto_modelo')
        setSelectedSortField('data_criacao')
        setSortDirection('desc')
        setRecordsPerPage(10)
        setCurrentPage(0)
        loadMovimentacoes(0)
    }

    // Função para abrir modal de visualização
    const openViewModal = async (movimentacao) => {
        try {
            const result = await getMovimentacaoId(movimentacao.movimentacao_id)
            if (result && result.data) {
                setViewingMovimentacao(result.data)
                setIsViewModalOpen(true)
            } else {
                toast.error('Erro ao carregar detalhes da movimentação')
            }
        } catch (error) {
            console.error('Erro ao carregar detalhes da movimentação:', error)
            toast.error('Erro ao carregar detalhes da movimentação')
        }
    }

    // Função para fechar modal de visualização
    const closeViewModal = () => {
        setIsViewModalOpen(false)
        setViewingMovimentacao(null)
    }

    const openImageModal = (imageSrc, imageAlt = 'Imagem da movimentação') => {
        setExpandedImage(imageSrc)
        setExpandedImageAlt(imageAlt)
        setIsImageModalOpen(true)
    }

    const closeImageModal = () => {
        setIsImageModalOpen(false)
        setExpandedImage('')
        setExpandedImageAlt('')
    }

    // Função para próxima página
    const handleNext = () => {
        const nextPage = currentPage + 1
        setCurrentPage(nextPage)
        loadMovimentacoes(nextPage)
    }

    // Função para página anterior
    const handlePrevious = () => {
        if (currentPage > 0) {
            const prevPage = currentPage - 1
            setCurrentPage(prevPage)
            loadMovimentacoes(prevPage)
        }
    }

    // Carregar dados iniciais
    useEffect(() => {
        if (id) {
            loadMovimentacoes()
        }
    }, [id])

    // Buscar automaticamente quando campos de ordenação ou direção mudam
    useEffect(() => {
        if (id && (selectedSortField || sortDirection)) {
            loadMovimentacoes()
        }
    }, [selectedSortField, sortDirection])

    // Função para validar base64
    const isValidBase64 = (str) => {
        try {
            return btoa(atob(str)) === str
        } catch (err) {
            return false
        }
    }

    // Função para criar src da imagem
    const createImageSrc = (base64String) => {
        if (!base64String || base64String === 'null' || base64String === 'Base64') {
            return ''
        }

        // Se já tem o prefixo data:image, retorna como está
        if (base64String.startsWith('data:image/')) {
            return base64String
        }

        // Se é base64 válido, adiciona o prefixo
        if (isValidBase64(base64String)) {
            return `data:image/webp;base64,${base64String}`
        }

        // Se não é base64 válido, tenta usar como URL direta
        return base64String
    }

    // Função para verificar se a imagem deve ser exibida
    const shouldShowImage = (imagePath) => {
        const shouldShow = imagePath && imagePath !== null && imagePath !== 'null' && imagePath.trim() !== '' && imagePath !== 'Base64'
        return shouldShow
    }

    // Função para formatar data
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('pt-BR')
    }

    // Função para formatar CPF/CNPJ
    const formatCPFCNPJ = (cpfCnpj) => {
        if (!cpfCnpj) return 'Não informado'

        // Remove caracteres não numéricos
        const numbers = cpfCnpj.replace(/\D/g, '')

        if (numbers.length === 11) {
            // CPF: 000.000.000-00
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        } else if (numbers.length === 14) {
            // CNPJ: 00.000.000/0000-00
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        }

        return cpfCnpj // Retorna original se não conseguir formatar
    }

    // Função para formatar telefone
    const formatPhone = (phone) => {
        if (!phone) return 'Não informado'

        // Remove caracteres não numéricos
        const numbers = phone.replace(/\D/g, '')

        if (numbers.length === 11) {
            // Celular: (00) 00000-0000
            return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
        } else if (numbers.length === 10) {
            // Fixo: (00) 0000-0000
            return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        }

        return phone // Retorna original se não conseguir formatar
    }

    // Função para obter tipo do cliente
    const getClienteTipo = (tipo) => {
        switch (tipo) {
            case 0: return { label: 'Fornecedor', color: 'text-blue-600', bgColor: 'bg-blue-100' }
            case 1: return { label: 'Cliente', color: 'text-green-600', bgColor: 'bg-green-100' }
            default: return { label: 'Desconhecido', color: 'text-gray-600', bgColor: 'bg-gray-100' }
        }
    }

    // Usar dados diretamente do estado global (sem filtros locais)
    const movimentacoes = states.dataMovimentacoes || []

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/30">
            {/* Header Artístico */}
            <div className="relative bg-gradient-to-br from-blue-50/80 via-white/80 to-cyan-50/80 backdrop-blur-sm border-b border-gradient-to-r from-blue-200/50 to-cyan-200/50 sticky top-0 z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => navigate(-1)} className="flex items-center border border-blue-200 rounded-2xl p-2 hover:bg-blue-100 transition-all duration-300">   <ArrowLeftIcon className="w-5 h-5" /> </button>
                                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-cyan-800 bg-clip-text text-transparent">
                                    Histórico de Movimentação do Cliente
                                </h1>
                            </div>
                            <p className="mt-3 text-lg text-gray-600 font-medium">
                                Acompanhe o fluxo completo de movimentação de um cliente
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conteúdo Principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Informações do Cliente */}
                {selectedCliente && (
                    <div className="relative bg-gradient-to-br from-blue-50/80 via-white/80 to-cyan-50/80 backdrop-blur-sm rounded-3xl shadow-lg border border-blue-100/50 mb-8 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full -translate-y-12 translate-x-12"></div>
                        <div className="relative p-6">
                            <div className="flex items-center mb-4">
                                <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-600 to-cyan-600 shadow-lg mr-4">
                                    <UserIcon className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-cyan-800 bg-clip-text text-transparent">
                                    Informações do Cliente
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Nome</label>
                                    <p className="text-lg font-semibold text-gray-900">{selectedCliente.nome}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
                                    <p className="text-lg font-semibold text-gray-900">{selectedCliente.email}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Telefone</label>
                                    <p className="text-lg font-semibold text-gray-900">{selectedCliente.telefone}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Tipo</label>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getClienteTipo(selectedCliente.tipo).bgColor} ${getClienteTipo(selectedCliente.tipo).color}`}>
                                        {getClienteTipo(selectedCliente.tipo).label}
                                    </span>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">CPF/CNPJ</label>
                                    <p className="text-sm text-gray-700">{formatCPFCNPJ(selectedCliente.cpf_cnpj)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Telefone</label>
                                    <p className="text-sm text-gray-700">{formatPhone(selectedCliente.telefone)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Data de Criação</label>
                                    <p className="text-sm text-gray-700">{formatDate(selectedCliente.data_criacao)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Última Atualização</label>
                                    <p className="text-sm text-gray-700">{formatDate(selectedCliente.data_atualizacao)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pesquisa Avançada */}
                <div className="relative bg-gradient-to-br from-blue-50/80 via-white/80 to-cyan-50/80 backdrop-blur-sm rounded-3xl shadow-lg border border-blue-100/50 mb-8 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="relative p-6">
                        <div className="flex items-center mb-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-600 to-cyan-600 shadow-lg mr-4">
                                <MagnifyingGlassIcon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-cyan-800 bg-clip-text text-transparent">
                                Pesquisa Avançada
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Termo de Busca</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch()
                                        }
                                    }}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Digite para buscar..."
                                    className="w-full px-4 py-3 border border-blue-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campo de Filtro</label>
                                <select
                                    value={selectedFilterField}
                                    onChange={(e) => setSelectedFilterField(e.target.value)}
                                    className="w-full px-4 py-3 border border-blue-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    {filterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campo de Ordenação</label>
                                <select
                                    value={selectedSortField}
                                    onChange={(e) => {
                                        setSelectedSortField(e.target.value)
                                        setCurrentPage(0)
                                        loadMovimentacoes(0)
                                    }}
                                    className="w-full px-4 py-3 border border-blue-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    {sortOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Direção de Ordenação</label>
                                <select
                                    value={sortDirection}
                                    onChange={(e) => {
                                        setSortDirection(e.target.value)
                                        setCurrentPage(0)
                                        loadMovimentacoes(0)
                                    }}
                                    className="w-full px-4 py-3 border border-blue-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    {sortDirectionOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Registros por Página</label>
                                <select
                                    value={recordsPerPage}
                                    onChange={(e) => {
                                        setRecordsPerPage(Number(e.target.value))
                                        setCurrentPage(0)
                                        loadMovimentacoes(0)
                                    }}
                                    className="w-full px-4 py-3 border border-blue-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value={5}>5 registros</option>
                                    <option value={10}>10 registros</option>
                                    <option value={20}>20 registros</option>
                                    <option value={50}>50 registros</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={handleSearch}
                                className="flex-1 group relative inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 via-cyan-700 to-cyan-700 hover:from-blue-700 hover:via-cyan-800 hover:to-cyan-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
                            >
                                <div className="relative z-10 flex items-center">
                                    <MagnifyingGlassIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Buscar</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>

                            <button
                                onClick={handleClear}
                                className="flex-1 group relative inline-flex items-center justify-center px-6 py-3 border border-blue-300 rounded-2xl shadow-lg text-sm font-semibold text-blue-700 bg-gradient-to-r from-blue-50 to-blue-50 hover:from-blue-100 hover:to-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
                            >
                                <div className="relative z-10 flex items-center">
                                    <XMarkIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Limpar</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Lista de Movimentações */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Histórico de Movimentações</h2>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Carregando movimentações...</p>
                                </div>
                            </div>
                        ) : movimentacoes.length > 0 ? (
                            <>
                                {/* Header da Lista - Desktop */}
                                <div className="hidden lg:block px-3 py-2 bg-gray-50 border-b border-gray-200">
                                    <div className="grid grid-cols-12 gap-1 items-center text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[1200px]">
                                        <div className="col-span-1 flex items-center justify-center">
                                            <TruckIcon className="w-3 h-3 mr-1" />
                                            Ações
                                        </div>
                                        <div className="col-span-1 flex items-center justify-center">
                                            <UserIcon className="w-3 h-3 mr-1" />
                                            Usuário
                                        </div>
                                        <div className="col-span-1 flex items-center justify-center">
                                            <InformationCircleIcon className="w-3 h-3 mr-1" />
                                            Código NET
                                        </div>
                                        <div className="col-span-2 flex items-center justify-center">
                                            <TruckIcon className="w-3 h-3 mr-1" />
                                            Produto
                                        </div>
                                        <div className="col-span-2 flex items-center justify-center">
                                            <UserIcon className="w-3 h-3 mr-1" />
                                            Responsável
                                        </div>
                                        <div className="col-span-1 flex items-center justify-center">
                                            <MapPinIcon className="w-3 h-3 mr-1" />
                                            Origem
                                        </div>
                                        <div className="col-span-2 flex items-center justify-center">
                                            <MapPinIcon className="w-3 h-3 mr-1" />
                                            Destino
                                        </div>
                                        <div className="col-span-1 flex items-center justify-center">
                                            <CalendarIcon className="w-3 h-3 mr-1" />
                                            Data
                                        </div>
                                        <div className="col-span-1 flex items-center justify-center">
                                            <ChartBarIcon className="w-3 h-3 mr-1" />
                                            Status
                                        </div>
                                    </div>
                                </div>

                                {/* Lista de Movimentações */}
                                <div className="divide-y divide-gray-200">
                                    {movimentacoes.map((movimentacao) => {
                                        const tipo = movimentacaoTipos.find(t => t.value === movimentacao.movimentacao_tipo)
                                        const IconComponent = tipo?.icon || TruckIcon

                                        return (
                                            <div
                                                key={movimentacao.movimentacao_id}
                                                className="px-3 py-2 hover:bg-gray-50 transition-colors duration-200"
                                            >
                                                {/* Desktop Layout */}
                                                <div className="hidden lg:block">
                                                    <div className="grid grid-cols-12 gap-1 items-center min-w-[1200px]">
                                                        {/* Ações */}
                                                        <div className="col-span-1 flex items-center justify-center">
                                                            <button
                                                                onClick={() => openViewModal(movimentacao)}
                                                                className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors duration-200"
                                                                title="Visualizar"
                                                            >
                                                                <EyeIcon className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                        {/* Usuário Responsável */}
                                                        <div className="col-span-1 text-center">
                                                            <div className="text-xs font-medium text-gray-900 truncate">
                                                                {movimentacao.movimentacao_usuario || 'N/A'}
                                                            </div>
                                                        </div>

                                                        {/* Código NET - DESTAQUE */}
                                                        <div className="col-span-1 flex justify-center">
                                                            <div className="inline-flex items-center px-1 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                                                                #{movimentacao.produto_codigo_net}
                                                            </div>
                                                        </div>

                                                        {/* Produto */}
                                                        <div className="col-span-2 text-center">
                                                            <div className="text-xs font-medium text-gray-900 truncate">
                                                                {movimentacao.produto_modelo}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {movimentacao.produto_serial}
                                                            </div>
                                                        </div>

                                                        {/* Responsável */}
                                                        <div className="col-span-2 text-center">
                                                            <div className="text-xs font-medium text-gray-900 truncate">
                                                                {movimentacao.movimentacao_responsavel_entrega}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {movimentacao.movimentacao_email_responsavel_entrega}
                                                            </div>
                                                        </div>

                                                        {/* Origem */}
                                                        <div className="col-span-1 text-center">
                                                            <div className="text-xs font-medium text-gray-900 truncate">
                                                                {movimentacao.entidade_origem_nome || 'N/A'}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {movimentacao.local_origem_nome || 'N/A'}
                                                            </div>
                                                        </div>

                                                        {/* Destino */}
                                                        <div className="col-span-2 text-center">
                                                            <div className="text-xs font-medium text-gray-900 truncate">
                                                                {movimentacao.entidade_destino_nome || 'N/A'}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {movimentacao.local_nome || 'N/A'}
                                                            </div>
                                                        </div>

                                                        {/* Data */}
                                                        <div className="col-span-1 text-center">
                                                            <div className="text-xs text-gray-900">
                                                                {new Date(movimentacao.movimentacao_data_movimentacao).toLocaleDateString('pt-BR')}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {new Date(movimentacao.movimentacao_data_movimentacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        </div>

                                                        {/* Status */}
                                                        <div className="col-span-1 flex justify-center">
                                                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${tipo?.color || 'bg-gray-500'}`}>
                                                                <IconComponent className="w-2 h-2 mr-0.5" />
                                                                {tipo?.label || 'Desconhecido'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Mobile Layout */}
                                                <div className="lg:hidden">
                                                    <div className="space-y-3">
                                                        {/* Header Mobile */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                                                                    #{movimentacao.produto_codigo_net}
                                                                </div>
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${tipo?.color || 'bg-gray-500'}`}>
                                                                    <IconComponent className="w-3 h-3 mr-1" />
                                                                    {tipo?.label || 'Desconhecido'}
                                                                </span>
                                                            </div>
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => openViewModal(movimentacao)}
                                                                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200"
                                                                    title="Visualizar"
                                                                >
                                                                    <EyeIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Informações Mobile */}
                                                        <div className="space-y-2">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Produto:</span>
                                                                <span className="text-sm text-gray-900">{movimentacao.produto_modelo}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Serial:</span>
                                                                <span className="text-sm text-gray-900">{movimentacao.produto_serial}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Responsável:</span>
                                                                <span className="text-sm text-gray-900">{movimentacao.movimentacao_responsavel_entrega}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Origem:</span>
                                                                <span className="text-sm text-gray-900">{movimentacao.entidade_origem_nome || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Destino:</span>
                                                                <span className="text-sm text-gray-900">{movimentacao.entidade_destino_nome || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-500">Data:</span>
                                                                <span className="text-sm text-gray-900">
                                                                    {new Date(movimentacao.movimentacao_data_movimentacao).toLocaleDateString('pt-BR')} {new Date(movimentacao.movimentacao_data_movimentacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="px-6 py-8 text-center text-gray-500">
                                <ArchiveBoxIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma movimentação encontrada</h3>
                                <p className="text-gray-500 mb-6">Este cliente ainda não possui movimentações registradas.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Paginação */}
                {movimentacoes.length > 0 && (
                    <div className="flex items-center justify-between mt-8">
                        <div className="text-sm text-gray-700">
                            Mostrando {((currentPage) * recordsPerPage) + 1} a {Math.min((currentPage + 1) * recordsPerPage, totalRecords)} de {totalRecords} movimentações
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handlePrevious}
                                disabled={currentPage === 0}
                                className="p-2 text-gray-600 bg-white rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            >
                                <ChevronLeftIcon className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 text-gray-700 bg-white rounded-lg border">
                                Página {currentPage + 1}
                            </span>
                            <button
                                onClick={handleNext}
                                disabled={movimentacoes.length < recordsPerPage}
                                className="p-2 text-gray-600 bg-white rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            >
                                <ChevronRightIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Carregando movimentações...</h3>
                        <p className="text-gray-500">Aguarde enquanto buscamos as informações do cliente.</p>
                    </div>
                )}

                {/* Estado vazio */}
                {!selectedCliente && !loading && (
                    <div className="text-center py-12">
                        <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Cliente não encontrado</h3>
                        <p className="text-gray-500">O cliente com ID {id} não foi encontrado ou não possui movimentações registradas.</p>
                    </div>
                )}

                {selectedCliente && movimentacoes.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma movimentação encontrada</h3>
                        <p className="text-gray-500">Nenhuma movimentação corresponde aos filtros aplicados.</p>
                    </div>
                )}

                {selectedCliente && (!movimentacoes || movimentacoes.length === 0) && !loading && (
                    <div className="text-center py-12">
                        <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma movimentação encontrada</h3>
                        <p className="text-gray-500">Este cliente ainda não possui movimentações registradas.</p>
                    </div>
                )}
            </div>

            {/* Modal de Visualização */}
            {isViewModalOpen && viewingMovimentacao && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                        {/* Header do Modal */}
                        <div className="relative shrink-0 bg-gradient-to-r from-sky-600 via-blue-700 to-blue-700 rounded-t-3xl p-6">
                            <div className="absolute inset-0 bg-gradient-to-r from-sky-500/10 to-blue-500/10 rounded-t-3xl"></div>
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm mr-4">
                                        <EyeIcon className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-white">
                                            Detalhes da Movimentação
                                        </h2>
                                        <p className="text-sky-100 mt-1">
                                            Visualize todas as informações da movimentação
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeViewModal}
                                    className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors duration-200"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Conteúdo do Modal */}
                        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
                            {/* Fluxo de Movimentação */}
                            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                                <div className="flex items-center mb-4">
                                    <ArrowsRightLeftIcon className="w-6 h-6 text-blue-600 mr-2" />
                                    <h3 className="text-lg font-semibold text-blue-800">Fluxo da Movimentação</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Origem */}
                                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                                        <div className="flex items-center mb-3">
                                            <ArrowUturnLeftIcon className="w-5 h-5 text-green-600 mr-2" />
                                            <h4 className="text-sm font-semibold text-green-800">ORIGEM</h4>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block text-xs font-medium text-green-700">Entidade</label>
                                                <p className="text-sm font-semibold text-gray-900">{viewingMovimentacao.entidade_origem_nome || 'Não informado'}</p>
                                            </div>
                                            {viewingMovimentacao.local_origem_nome && (
                                                <div>
                                                    <label className="block text-xs font-medium text-green-700">Local</label>
                                                    <p className="text-sm text-gray-900">📍 {viewingMovimentacao.local_origem_nome}</p>
                                                </div>
                                            )}
                                            {viewingMovimentacao.departamento_origem_nome && (
                                                <div>
                                                    <label className="block text-xs font-medium text-green-700">Departamento</label>
                                                    <p className="text-sm text-gray-900">🏢 {viewingMovimentacao.departamento_origem_nome}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Destino */}
                                    <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                                        <div className="flex items-center mb-3">
                                            <TruckIcon className="w-5 h-5 text-purple-600 mr-2" />
                                            <h4 className="text-sm font-semibold text-purple-800">DESTINO</h4>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block text-xs font-medium text-purple-700">Entidade</label>
                                                <p className="text-sm font-semibold text-gray-900">{viewingMovimentacao.entidade_destino_nome || 'Não informado'}</p>
                                            </div>
                                            {viewingMovimentacao.local_nome && (
                                                <div>
                                                    <label className="block text-xs font-medium text-purple-700">Local</label>
                                                    <p className="text-sm text-gray-900">📍 {viewingMovimentacao.local_nome}</p>
                                                </div>
                                            )}
                                            {viewingMovimentacao.departamento_nome && (
                                                <div>
                                                    <label className="block text-xs font-medium text-purple-700">Departamento</label>
                                                    <p className="text-sm text-gray-900">🏢 {viewingMovimentacao.departamento_nome}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Informações Principais */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações da Movimentação</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">ID da Movimentação</label>
                                            <p className="text-sm text-gray-900 font-mono">{viewingMovimentacao.movimentacao_id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Contador</label>
                                            <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_contador}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Tipo</label>
                                            <p className="text-sm text-gray-900">
                                                {movimentacaoTipos.find(t => t.value === viewingMovimentacao.movimentacao_tipo)?.label || 'Desconhecido'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Data de Movimentação</label>
                                            <p className="text-sm text-gray-900">
                                                {formatDate(viewingMovimentacao.movimentacao_data_movimentacao)}
                                            </p>
                                        </div>
                                        {viewingMovimentacao.movimentacao_data_saida && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-600">Data de Saída</label>
                                                <p className="text-sm text-gray-900">
                                                    {formatDate(viewingMovimentacao.movimentacao_data_saida)}
                                                </p>
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Usuário Responsável</label>
                                            <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_usuario || 'Não informado'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Fornecedor e Responsável</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Fornecedor</label>
                                            <p className="text-sm text-gray-900">{viewingMovimentacao.fornecedor_nome || 'Não informado'}</p>
                                        </div>
                                        {viewingMovimentacao.movimentacao_responsavel_entrega && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-600">Responsável pela Entrega</label>
                                                <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_responsavel_entrega}</p>
                                            </div>
                                        )}
                                        {viewingMovimentacao.movimentacao_email_responsavel_entrega && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-600">Email do Responsável</label>
                                                <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_email_responsavel_entrega}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Detalhes da Entrega */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhes da Entrega</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Localização</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_localizacao || 'Não informado'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Responsável</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_responsavel_entrega || 'Não informado'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Email do Responsável</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_email_responsavel_entrega || 'Não informado'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Observação</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.movimentacao_observacao || 'Sem observação'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Imagens */}
                            {(viewingMovimentacao.movimentacao_foto1 || viewingMovimentacao.movimentacao_foto2 || viewingMovimentacao.movimentacao_foto3) && (
                                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                                    <div className="flex items-center mb-4">
                                        <div className="p-3 rounded-xl bg-gradient-to-r from-gray-500 to-blue-600 shadow-lg mr-3">
                                            <PhotoIcon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">Imagens da Movimentação</h3>
                                            <p className="text-gray-600 text-sm">Fotos registradas durante a movimentação</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {viewingMovimentacao.movimentacao_foto1 && (
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                className="relative group cursor-pointer rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all duration-300 hover:shadow-lg"
                                                onClick={() => openImageModal(
                                                    viewingMovimentacao.movimentacao_foto1_path
                                                        ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto1_path}`
                                                        : `/movimentacao/${viewingMovimentacao.movimentacao_foto1}`,
                                                    'Foto 1 da movimentação'
                                                )}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        openImageModal(
                                                            viewingMovimentacao.movimentacao_foto1_path
                                                                ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto1_path}`
                                                                : `/movimentacao/${viewingMovimentacao.movimentacao_foto1}`,
                                                            'Foto 1 da movimentação'
                                                        )
                                                    }
                                                }}
                                            >
                                                <div className="relative overflow-hidden rounded-lg">
                                                    <img
                                                        src={viewingMovimentacao.movimentacao_foto1_path ?
                                                            `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto1_path}` :
                                                            `/movimentacao/${viewingMovimentacao.movimentacao_foto1}`
                                                        }
                                                        alt="Foto 1 da movimentação"
                                                        className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105"
                                                        onError={(e) => {
                                                            console.error('Erro ao carregar imagem 1:', e)
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg"></div>
                                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                                                        Foto 1
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {viewingMovimentacao.movimentacao_foto2 && (
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                className="relative group cursor-pointer rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all duration-300 hover:shadow-lg"
                                                onClick={() => openImageModal(
                                                    viewingMovimentacao.movimentacao_foto2_path
                                                        ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto2_path}`
                                                        : `/movimentacao/${viewingMovimentacao.movimentacao_foto2}`,
                                                    'Foto 2 da movimentação'
                                                )}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        openImageModal(
                                                            viewingMovimentacao.movimentacao_foto2_path
                                                                ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto2_path}`
                                                                : `/movimentacao/${viewingMovimentacao.movimentacao_foto2}`,
                                                            'Foto 2 da movimentação'
                                                        )
                                                    }
                                                }}
                                            >
                                                <div className="relative overflow-hidden rounded-lg">
                                                    <img
                                                        src={viewingMovimentacao.movimentacao_foto2_path ?
                                                            `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto2_path}` :
                                                            `/movimentacao/${viewingMovimentacao.movimentacao_foto2}`
                                                        }
                                                        alt="Foto 2 da movimentação"
                                                        className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105"
                                                        onError={(e) => {
                                                            console.error('Erro ao carregar imagem 2:', e)
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg"></div>
                                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                                                        Foto 2
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {viewingMovimentacao.movimentacao_foto3 && (
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                className="relative group cursor-pointer rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all duration-300 hover:shadow-lg"
                                                onClick={() => openImageModal(
                                                    viewingMovimentacao.movimentacao_foto3_path
                                                        ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto3_path}`
                                                        : `/movimentacao/${viewingMovimentacao.movimentacao_foto3}`,
                                                    'Foto 3 da movimentação'
                                                )}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        openImageModal(
                                                            viewingMovimentacao.movimentacao_foto3_path
                                                                ? `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto3_path}`
                                                                : `/movimentacao/${viewingMovimentacao.movimentacao_foto3}`,
                                                            'Foto 3 da movimentação'
                                                        )
                                                    }
                                                }}
                                            >
                                                <div className="relative overflow-hidden rounded-lg">
                                                    <img
                                                        src={viewingMovimentacao.movimentacao_foto3_path ?
                                                            `data:image/webp;base64,${viewingMovimentacao.movimentacao_foto3_path}` :
                                                            `/movimentacao/${viewingMovimentacao.movimentacao_foto3}`
                                                        }
                                                        alt="Foto 3 da movimentação"
                                                        className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105"
                                                        onError={(e) => {
                                                            console.error('Erro ao carregar imagem 3:', e)
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg"></div>
                                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                                                        Foto 3
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Informações do Produto */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Produto</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Código Net</label>
                                        <p className="text-sm text-gray-900 font-semibold">{viewingMovimentacao.produto_codigo_net}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Modelo</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.produto_modelo}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Serial</label>
                                        <p className="text-sm text-gray-900 font-mono">{viewingMovimentacao.produto_serial}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Equipamento</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.equipamento_nome}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Formato</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.equipamento_formato_nome}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Marca</label>
                                        <p className="text-sm text-gray-900">{viewingMovimentacao.marca_nome}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Condição</label>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${viewingMovimentacao.produto_condicao === 0 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                            {viewingMovimentacao.produto_condicao === 0 ? 'Novo' : 'Usado'}
                                        </span>
                                    </div>
                                    {viewingMovimentacao.produto_data_aquisicao && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Data de Aquisição</label>
                                            <p className="text-sm text-gray-900">{formatDate(viewingMovimentacao.produto_data_aquisicao)}</p>
                                        </div>
                                    )}
                                    {viewingMovimentacao.produto_numero_nota && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600">Número da Nota</label>
                                            <p className="text-sm text-gray-900">{viewingMovimentacao.produto_numero_nota}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Observação do Produto */}
                                {viewingMovimentacao.produto_observacao && (
                                    <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                                        <label className="block text-sm font-medium text-blue-800 mb-2">Observação do Produto</label>
                                        <p className="text-sm text-gray-700">{viewingMovimentacao.produto_observacao}</p>
                                    </div>
                                )}
                            </div>

                            {/* Datas */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações de Data</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Data de Criação</label>
                                        <p className="text-sm text-gray-900">
                                            {formatDate(viewingMovimentacao.data_criacao)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Última Atualização</label>
                                        <p className="text-sm text-gray-900">
                                            {formatDate(viewingMovimentacao.data_atualizacao)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer do Modal */}
                        <div className="flex shrink-0 items-center justify-end border-t border-gray-200 p-6">
                            <button
                                onClick={closeViewModal}
                                className="px-6 py-3 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-all duration-300"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ZoomableImageModal
                isOpen={isImageModalOpen}
                onClose={closeImageModal}
                src={expandedImage}
                alt={expandedImageAlt}
            />
        </div>
    )
}

export default MovimentacaoCliente